import { asMutableType, deepFreeze, } from '@tymia/shared';
import { entries, keys, once, pick, } from 'remeda';
import { searchBookableAppointmentsResponseSchema, } from '../appointment/AppointmentApi.js';
import { nullableEnum, pagingRequest, pagingResponse, } from '../validator.js';
import { validate, } from '../validator.node.js';
export const userModuleUrl = import.meta.url;
export const salutation = ['Herr', 'Frau', 'Divers'];
export const Roles = {
    guest: 1,
    registered: 2,
    admin: 4,
    assistant: 8,
    // TODO: rename into specialist
    therapist: 16,
    customeradmin: 32,
    therapistdrs: 64,
};
export const userInsuranceType = asMutableType(Object.freeze([
    'legally',
    'privately',
]));
export const TREATMENT_METHODS = deepFreeze([
    ['Behavioral Therapy', ['Verhaltenstherapie', 'kognitive Verhaltenstherapie', 'KVT']],
    ['Psychodynamic Therapy', ['Tiefenpsychologisch fundierte Psychotherapie', 'tiefenpsychologische Therapie']],
    ['Coaching', ['Coaching']],
    ['Systemic Therapy', ['Systemische Therapie']],
    ['Psychological Counseling', ['Psychologische Beratung']],
    ['Schema Therapy', ['Schematherapie']],
    ['Relaxation Techniques', [
            'Entspannungsverfahren',
            'Autogenes Training',
            'PMR',
            'Yoga',
            'Meditation',
            'Tai-Chi',
            'Qigong',
            'progressive Muskelentspannung nach Jacobson',
        ]],
    ['Dialectical Behavior Therapy', [
            'Dialektisch-behaviorale Therapie (DBT)',
            'DBT',
            'Dialektisch-behaviorale Therapie',
        ]],
    ['Person-Centered Therapy', ['Gesprächstherapie']],
    ['Hypnotherapy', ['Hypnotherapie', 'Hypnose']],
    ['Integrative Therapy', ['Integrative Therapie']],
    ['Somatic Therapy', ['Körperorientierte Verfahren']],
    ['EMDR', [
            'EMDR',
            'Eye Movement Desensitization and Reprocessing',
            'Desensibilisierung und Verarbeitung durch Augenbewegung',
        ]],
    ['Humanistic Therapy', ['Humanistische Verfahren']],
    ['Art Therapy', ['Kunsttherapie']],
    ['Psychoanalysis', ['Psychoanalyse', 'analytische Therapie']],
    ['Supervision', ['Supervision']],
]);
export function getKeyForTreatmentMethod(value, onlyKnown = false) {
    for (const [key, synonyms] of TREATMENT_METHODS) {
        if ([key, ...synonyms].includes(value)) {
            return key;
        }
    }
    if (onlyKnown) {
        throw new Error(`Unknown treatment method: ${value}`);
    }
    return value;
}
export const THERAPEUTIC_FOCUS = deepFreeze([
    ['ADHD', [
            'ADHS',
            'ADS',
            'Aufmerksamkeitsdefizit- / Hyperaktivitätsstörung',
            'Aufmerksamkeitsdefizit-Hyperaktivitätsstörung',
        ]],
    ['Anxiety and Panic Disorders', ['Angst- und Panikstörungen', 'Panikstörung', 'Panikattacken', 'Phobien']],
    ['Adjustment Disorder', ['Anpassungsstörung', 'Anpassungsstörung und Lebenskrisen', 'Lebenskrise']],
    ['Bipolar Disorder', ['Bipolare Störung']],
    ['Burnout', ['Burnout', '„Burnout“ und stressassoziierte Erkrankungen', 'Erschöpfung']],
    ['Social Phobia', ['Soziale Phobie']],
    ['Generalized Anxiety Disorder', ['Generalisierte Angststörung']],
    ['Agoraphobia', ['Agoraphobie']],
    ['Depression', [
            'Depression',
            'Depressionen',
            'depressive Entwicklung',
            'depressive Verstimmung',
            'Niedergeschlagenheit',
        ]],
    ['Borderline Disorder', ['Borderline-Störung', 'Emotionale Instabilität', 'Borderline', 'emotional instabil']],
    ['Eating Disorders', ['Essstörung', 'Anorexie', 'Bulimie', 'Binge']],
    ['Family Therapy', ['Familientherapie']],
    ['Group Therapy', ['Gruppentherapie']],
    ['Couples Therapy', ['Paartherapie', 'Paargespräche']],
    ['Personality Disorders', ['Persönlichkeitsstörungen', 'Persönlichkeitsstörung']],
    ['Post-Traumatic Stress Disorder', ['Posttraumatische Belastungsstörung', 'Trauma', 'PTBS']],
    ['Psycho-Oncology', ['Psychoonkologie']],
    ['Psychocardiology', ['Psychokardiologie']],
    ['Psychosis - Schizophrenia', ['Psychose - Schizophrenie']],
    ['Psychosomatic Disorders', ['Psychosomatische Beschwerden', 'somatoform', 'psychosomatisch']],
    ['Pain', ['Schmerzen']],
    ['Sexuality', ['Sexualität']],
    ['Addiction', ['Sucht', 'Alkoholabhängigkeit', 'Drogen']],
    ['Child and Adolescent Therapy', ['Therapie für Kinder und Jugendliche', 'Jugendliche']],
    ['Grief', ['Trauer']],
    ['Obsessive-Compulsive Disorder', ['Zwangsstörung', 'Zwang', 'Zwangsgedanken', 'Zwangshandlungen']],
]);
export function getKeyForTherapeuticFocus(value, onlyKnown = false) {
    for (const [key, synonyms] of THERAPEUTIC_FOCUS) {
        if ([key, ...synonyms].includes(value)) {
            return key;
        }
    }
    if (onlyKnown) {
        throw new Error(`Unknown therapeutic focus: ${value}`);
    }
    return value;
}
export const coverageTypeMap = {
    'Private Health Insurance': 'Private Krankenkassen',
    'Statutory Health Insurance': 'Gesetzliche Krankenkassen',
    'Self-Payment': 'Selbstzahlende',
    'Reimbursement Procedure': 'Kostenerstattungsverfahren',
};
export const coverageTypes = asMutableType(Object.freeze(keys(coverageTypeMap)));
export function getKeyForCoverageType(value, onlyKnown = false) {
    for (const [key, german] of entries(coverageTypeMap)) {
        if ([key, german].includes(value)) {
            return key;
        }
    }
    if (onlyKnown) {
        throw new Error(`Unknown coverage type: ${value}`);
    }
    return value;
}
export const userJobTitleType = asMutableType(Object.freeze([
    'psychologicalPsychotherapist',
    'psychologicalPsychotherapistChildren',
    'specialistPsychiatryPsychotherapy',
    'specialistPsychiatryPsychotherapyChildren',
    'specialistPsychosomatics',
    'physicianAdditionalQualificationPsychotherapy',
    'nonMedicalPractitionerPsychotherapy',
    'socialPedagogue',
    'coachOrConsultant',
]));
export const advertisingType = asMutableType(Object.freeze([
    'no',
    'show',
]));
export const invoiceAssessmentSessionsType = asMutableType(Object.freeze([
    'mark',
    'nomark',
]));
export const invoicePatientNumberType = asMutableType(Object.freeze([
    'show',
    'hide',
]));
export const appointmentOnsiteNotificationDefaultType = asMutableType(Object.freeze([
    'on',
    'off',
]));
export const salutationSearchableType = asMutableType(Object.freeze([
    'yes',
    'no',
]));
export const invoiceDigitalType = asMutableType(Object.freeze([
    'yes',
    'no',
]));
export const genderType = asMutableType(Object.freeze([
    'female',
    'male',
    'diverse',
]));
export function genderToSalutation(gender) {
    return {
        female: 'Frau',
        male: 'Herr',
        diverse: 'Divers',
    }[gender];
}
export const locationType = asMutableType(Object.freeze([
    'onSite',
    'remote',
]));
export const publicVisibilityType = asMutableType(Object.freeze([
    'public',
    'private',
]));
export const searchTherapistSortType = asMutableType(Object.freeze([
    'distance',
    'availability',
]));
export const userCommonOpenApiFields = {
    id: {
        type: 'number',
    },
    salutation: nullableEnum(salutation),
    title: {
        type: ['string', 'null'],
        nullable: true,
    },
    firstName: {
        type: 'string',
        minLength: 1,
    },
    lastName: {
        type: 'string',
        minLength: 1,
    },
    roles: {
        type: 'number',
        enum: [
            Roles.admin,
            Roles.assistant,
            Roles.therapist,
            Roles.customeradmin,
            Roles.customeradmin + Roles.therapist,
            Roles.therapistdrs,
        ],
    },
    email: {
        type: 'string',
        format: 'idn-email',
    },
    phoneCountry: {
        type: ['string', 'null'],
        nullable: true,
    },
    phone: {
        type: ['string', 'null'],
        nullable: true,
    },
    pinTransportMethod: {
        type: 'string',
        enum: ['none', 'email'],
    },
    deleted: {
        type: 'boolean',
    },
    street: {
        type: ['string', 'null'],
        nullable: true,
    },
    postalCode: {
        type: ['string', 'null'],
        nullable: true,
    },
    city: {
        type: ['string', 'null'],
        nullable: true,
    },
    languages: {
        type: 'array',
        items: {
            type: 'string',
            minLength: 2,
            maxLength: 2,
        },
        minItems: 1,
    },
};
export const userClientOpenApiFields = {
    // therapist or assistant can set patientNumber manually
    patientNumber: {
        type: ['string', 'number', 'null'],
        nullable: true,
    },
    birthday: {
        type: ['string', 'null'],
        format: 'iso-date-time',
        nullable: true,
    },
    insuranceType: nullableEnum(userInsuranceType),
    defaultUserDiagnosisId: {
        type: ['number', 'null'],
        nullable: true,
    },
    internalNote: {
        type: ['string', 'null'],
        nullable: true,
    },
    invoiceRemark: {
        type: ['string', 'null'],
        nullable: true,
    },
    careOf: {
        type: ['string', 'null'],
        nullable: true,
    },
    country: {
        type: ['string', 'null'],
        nullable: true,
    },
    invoiceAddress: {
        type: ['string', 'null'],
        nullable: true,
    },
    invoiceDigital: {
        type: ['string', 'null'],
        enum: [...invoiceDigitalType, null],
        nullable: true,
    },
    therapistId: {
        type: 'integer',
    },
};
export const userSpecialistOpenApiFields = {
    avatar: {
        type: ['string', 'null'],
        nullable: true,
    },
    website: {
        type: ['string', 'null'],
        nullable: true,
    },
    // it's required only for therapists
    // "other" is send as `null`
    jobTitleType: {
        type: ['string', 'null'],
        enum: [...userJobTitleType, null],
        nullable: true,
    },
    jobTitleFreetext: {
        type: ['string', 'null'],
        nullable: true,
    },
    treatmentMethods: {
        type: 'array',
        items: {
            type: 'string',
        },
    },
    therapeuticFocus: {
        type: 'array',
        items: {
            type: 'string',
        },
    },
    coverageTypes: {
        type: 'array',
        items: {
            type: 'string',
            enum: coverageTypes,
        },
    },
    therapistProfileText: {
        type: ['string', 'null'],
        nullable: true,
    },
    billingInformation: {
        type: ['string', 'null'],
        nullable: true,
    },
    vita: {
        type: ['string', 'null'],
        nullable: true,
    },
    billingApproval: {
        type: ['boolean', 'null'],
        nullable: true,
    },
    returnAddress: {
        type: ['string', 'null'],
        nullable: true,
    },
    invoiceSignature: {
        type: ['string', 'null'],
        nullable: true,
    },
    invoiceAssessmentSessions: {
        type: ['string', 'null'],
        enum: [...invoiceAssessmentSessionsType, null],
        nullable: true,
    },
    invoicePatientNumber: {
        type: ['string', 'null'],
        enum: [...invoicePatientNumberType, null],
        nullable: true,
    },
    appointmentOnsiteNotificationDefault: {
        type: ['string', 'null'],
        enum: [...appointmentOnsiteNotificationDefaultType, null],
        nullable: true,
    },
    mollieSubscriptionId: {
        type: ['string', 'null'],
        nullable: true,
    },
    advertising: {
        type: ['string', 'null'],
        enum: [...advertisingType, null],
        nullable: true,
    },
    salutationSearchable: {
        type: 'string',
        enum: salutationSearchableType,
    },
    publicVisibility: {
        type: ['string', 'null'],
        enum: [...publicVisibilityType, null],
        nullable: true,
    },
};
// * searchTherapist
const searchTherapistRequestSchema = {
    type: 'object',
    additionalProperties: false,
    properties: {
        address: {
            type: ['string', 'null'],
            minLength: 1,
            maxLength: 1_000,
        },
        therapeuticFocus: {
            type: 'array',
            items: {
                type: 'string',
            },
            minItems: 0,
            maxItems: 1_000,
            uniqueItems: true,
        },
        treatmentMethods: {
            type: 'array',
            items: {
                type: 'string',
            },
            minItems: 0,
            maxItems: 1_000,
            uniqueItems: true,
        },
        jobTitleTypes: {
            type: 'array',
            items: {
                type: 'string',
                enum: userJobTitleType,
            },
            minItems: 0,
            maxItems: 1_000,
            uniqueItems: true,
        },
        languages: {
            type: 'array',
            items: {
                type: 'string',
                minLength: 2,
                maxLength: 2,
            },
            minItems: 0,
            maxItems: 1_000,
            uniqueItems: true,
        },
        coverageTypes: {
            type: 'array',
            items: {
                type: 'string',
                enum: coverageTypes,
            },
            minItems: 0,
            maxItems: 1_000,
            uniqueItems: true,
        },
        gender: {
            type: 'array',
            items: {
                type: 'string',
                enum: genderType,
            },
            minItems: 0,
            maxItems: 3,
            uniqueItems: true,
        },
        location: {
            type: 'array',
            items: {
                type: 'string',
                enum: locationType,
            },
            minItems: 0,
            maxItems: 2,
            uniqueItems: true,
        },
        sort: {
            type: ['string', 'null'],
            enum: ['distance', 'availability', null],
        },
        // filtering inside the BookingComponent
        source: {
            type: 'string',
            enum: ['widget', 'search'],
        },
        therapistId: {
            type: 'number',
        },
        insuranseType: {
            type: 'string',
            enum: userInsuranceType,
        },
        meetingTypeId: {
            type: 'number',
        },
        appointmentsFrom: {
            type: 'string',
            format: 'date-time',
        },
        ...pagingRequest.properties,
    },
};
export function searchTherapistRequestValidate(props) {
    return validate(searchTherapistRequestSchema, props);
}
const searchTherapistResponseSchema = once(() => ({
    type: 'object',
    additionalProperties: false,
    properties: {
        status: {
            const: 'ok',
        },
        city: {
            type: 'object',
            additionalProperties: false,
            properties: {
                name: {
                    type: 'string',
                },
                description: {
                    type: ['string', 'null'],
                    nullable: true,
                },
                content: {
                    type: ['string', 'null'],
                    nullable: true,
                },
            },
            required: [
                'name',
            ],
        },
        languages: {
            type: 'array',
            items: {
                type: 'string',
                minLength: 2,
                maxLength: 2,
            },
            uniqueItems: true,
        },
        list: {
            type: 'array',
            items: {
                type: 'object',
                additionalProperties: false,
                properties: {
                    ...pick(userCommonOpenApiFields, [
                        'id', // absent in imported data
                        'salutation',
                        'title',
                        'firstName',
                        'lastName',
                        'phone',
                        'languages',
                        'street',
                        'postalCode',
                        'city',
                    ]),
                    ...pick(userSpecialistOpenApiFields, [
                        'avatar',
                        'website',
                        'jobTitleType',
                        'jobTitleFreetext',
                        'therapistProfileText',
                        'billingInformation',
                        'treatmentMethods',
                        'therapeuticFocus',
                        'coverageTypes',
                    ]),
                    customerId: {
                        type: 'number',
                    },
                    distance: {
                        type: 'number',
                    },
                    nextFreeAppointment: {
                        type: ['string', 'null'],
                        nullable: true,
                        format: 'date-time',
                    },
                    // set only for advertising
                    nextFreeRemoteAppointment: {
                        type: ['string', 'null'],
                        nullable: true,
                        format: 'date-time',
                    },
                    type: {
                        type: 'string',
                        enum: ['ad', 'available', 'with avatar', 'without avatar', 'external'],
                    },
                    appointments: searchBookableAppointmentsResponseSchema(),
                },
                required: [
                    'firstName',
                    'lastName',
                    'languages',
                    'street',
                    'postalCode',
                    'city',
                    'treatmentMethods',
                    'therapeuticFocus',
                    'coverageTypes',
                    'customerId',
                    'distance',
                    'nextFreeAppointment',
                    'nextFreeRemoteAppointment',
                    'type',
                ],
            },
            uniqueItems: true,
        },
        ...pagingResponse.properties,
    },
    required: [
        'status',
        'city',
        'languages',
        'list',
        ...pagingResponse.required,
    ],
}));
export function searchTherapistResponseValidate(props) {
    return validate(searchTherapistResponseSchema(), props);
}
export async function searchTherapist(baseUrl, props) {
    const result = await fetch(`${baseUrl}/api/therapist/search`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(props),
    });
    if (result.ok) {
        const data = await result.json();
        return data;
    }
    return {
        status: 'network error',
        httpStatus: result.status,
        message: await result.text(),
    };
}
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function postClientRequestSchema() {
    return {
        type: 'object',
        additionalProperties: false,
        properties: {
            customerId: {
                type: 'number',
            },
            therapistId: {
                type: 'number',
            },
            salutation: nullableEnum(salutation),
            title: {
                type: ['string', 'null'],
                nullable: true,
            },
            firstName: {
                type: 'string',
            },
            lastName: {
                type: 'string',
            },
            email: {
                type: 'string',
                format: 'idn-email',
            },
            phoneCountry: {
                type: ['string', 'null'],
                nullable: true,
            },
            phone: {
                type: ['string', 'null'],
                nullable: true,
            },
            language: {
                type: ['null'],
                nullable: true,
            },
            insuranceType: nullableEnum(userInsuranceType),
            birthday: {
                type: ['string', 'null'],
                format: 'iso-date-time',
                nullable: true,
            },
            // therapist or assistant can set patientNumber manually
            patientNumber: {
                type: ['string', 'null'],
                nullable: true,
            },
            street: {
                type: ['string', 'null'],
                nullable: true,
            },
            postalCode: {
                type: ['string', 'null'],
                nullable: true,
            },
            city: {
                type: ['string', 'null'],
                nullable: true,
            },
            careOf: {
                type: ['string', 'null'],
                nullable: true,
            },
            country: {
                type: ['string', 'null'],
                nullable: true,
            },
            internalNote: {
                type: ['string', 'null'],
                nullable: true,
            },
            invoiceRemark: {
                type: ['string', 'null'],
                nullable: true,
            },
            invoiceAddress: {
                type: ['string', 'null'],
                nullable: true,
            },
            invoiceDigital: {
                type: ['string', 'null'],
                enum: [...invoiceDigitalType, null],
                nullable: true,
            },
        },
        required: [
            'customerId',
            'therapistId',
            'firstName',
            'lastName',
            'email',
        ],
    };
}
//# sourceMappingURL=UserApi.js.map